import Typography from "@mui/material/Typography";
import React, {useEffect, useState} from "react";
import {
    Alert,
    ButtonBase,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    Paper,
    Tab,
    Tabs
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Box from "@mui/material/Box";
import PropTypes from "prop-types";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import {useNavigate} from "react-router";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {produce} from "immer";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import useMediaQuery from "@mui/material/useMediaQuery";
import {useTheme} from "@mui/material/styles";
import UpdateProductGroupNewCategory from "../Products/UpdateProductGroupNewCategory";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ConfirmationDialog from "../Dialogs/ConfirmationDialog";
import LoadingOverlay from "../Loading/LoadingOverlay";

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            {...other}
            style={{ width: '100%'}}
        >
            {value === index && (
                <Box sx={{ p: 2}}>
                    {children}
                </Box>
            )}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
}

const ButtonInTabs = ({ className, onClick, children }) => {
    return <Button className={className} onClick={onClick} children={children} />;
};

const ProductGroupCategoriesView = ({
                                        product,
                                        edit,
                                        setActiveForm,
                                        activeForm,
                                        allCategories,
                                        isSubmitting }) =>
{
    const [value, setValue] = useState(0);
    const [loading, setLoading] = useState(false);
    const [categoryWithNoItemsError, setCategoryWithNoItemsError] = useState(null);
    const navigate = useNavigate();
    const [relevantCategories, setRelevantCategories] = useState([]);
    const [addCategory, setAddCategory] = useState(false);
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('sm'));
    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const allCategoriesWithAllItems = allCategories.reduce((acc, category) => {
        acc[category.name] = {
            categoryId: category.id,
            name: category.name,
            productItems: category.productItems
        };
        return acc;
    }, {});

    const handleCategoryItemSelection = (event, checked, categoryName, itemId) => {
        setActiveForm(prevState => produce(prevState, draft => {
            const category = draft.allowedCategories.find(x => x.categoryName === categoryName);
            if (!category) return;
            const items = category.allowedItems;
            const item = items[itemId];

            if (checked) {
                if (!item) {
                    const itemInCategory = allCategoriesWithAllItems[categoryName]?.productItems?.find(item => item.id === itemId);
                    if (itemInCategory) {
                        items?.push(itemInCategory.id);
                    }
                }
            } else {
                category.allowedItems = items?.filter(id => id !== itemId);
            }
                
            setCategoryWithNoItemsError(category.allowedItems.length === 0 ? "Please select at least one item." : null)
        }));
    };
    
    const handleItemClick = (itemId, itemName) => {
        navigate(`/Products/${itemId}`,
            { state: {
                domain: { id: '/Products', name: 'Products' }, 
                    prev: { id: product.id, name: product.name}, 
                    next: {id: itemId, name: itemName}
                }
            });
    };
    
    const handleAddCategory = () => {
        setAddCategory(true);
    }

    const handleRemoveCategory = (category) => {
        setActiveForm(prevState => produce(prevState, draft => {
            const categoryIndex = draft.categories.findIndex(x => x.id === category.id);
            if (categoryIndex !== -1) {
                draft.categories.splice(categoryIndex, 1);
            }

            const allowedCategoryIndex = draft.allowedCategories.findIndex(x => x.categoryName === category.name);
            if (allowedCategoryIndex !== -1) {
                draft.allowedCategories.splice(allowedCategoryIndex, 1);
            }
        }));
    };

    const handleChecked = (category, item) => {
        return !!activeForm?.allowedCategories.find(i => i.categoryName === category.name)?.allowedItems.find(x => x === item.id);
    }

    useEffect(() => {
        const computeRelevantCategories = (productObject) => {
            const categories = productObject?.allowedCategories.map(allowedCategory => {
                const category = allCategoriesWithAllItems[allowedCategory.categoryName];
                if (category) {
                    // Filter product items based on AllowedItems IDs
                    const relevantItems = category.productItems.filter(item =>
                        allowedCategory.allowedItems.includes(item.id)
                    );

                    return {
                        id: category.categoryId,
                        name: category.name,
                        categoryName: allowedCategory.categoryName,
                        relevantItems: relevantItems,
                        allItems: category.productItems
                    };
                }
                return null;
            }).filter(category => category !== null);
            
            setRelevantCategories(categories);
        };

        if (edit) {
            computeRelevantCategories(activeForm);
        } else {
            computeRelevantCategories(product);
        }
    }, [product, activeForm]);
    
    return (
        <Paper>
            <Grid container>
                <Grid size={{ xs: 12 }}>
                    <Typography variant="h6" className={"detail-header-banner"} sx={{ color: 'white' }}>Categories</Typography>
                </Grid>
                <Grid size={{ xs: 12 }} >
                    <Box
                        sx={{ flexGrow: 2, 
                            backgroundColor: 'background.paper',
                            display: 'flex',
                            minHeight: 224, 
                            width: '100%!important' }}
                    >
                        <Tabs
                            orientation="vertical"
                            variant="scrollable"
                            value={(edit ? value > activeForm?.allowedCategories.length - 1 : value > product?.allowedCategories.length - 1) ? 0 : value || 0}
                            onChange={handleChange}
                            aria-label="Categories"
                            sx={{ borderRight: 1,
                                borderColor: 'divider', 
                                maxWidth: '150px', 
                                minWidth: '120px',
                                '& .MuiTabs-indicator': { display: addCategory ? 'none' : 'block' } }}
                        >
                            {edit 
                                ? 
                                activeForm?.allowedCategories?.filter(x => !x.newCategory)?.map((item, index) => (
                                    <Tab key={index} label={item.categoryName} {...a11yProps(index)} disabled={addCategory} />
                                ))
                                :
                                product?.allowedCategories.map((item, index) => (
                                    <Tab key={index} label={item.categoryName} {...a11yProps(index)} disabled={addCategory} />
                                ))
                            }
                            {edit && activeForm?.allowedCategories.filter(x => x.newCategory && x.newCategory === true)?.map((item, index) => (
                                <Tab key={index} 
                                     label={<Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <AddIcon sx={{ mr: 0.5, color: theme.palette.success.main }} />
                                        {item.categoryName}
                                    </Box>} 
                                     {...a11yProps(index)} 
                                     disabled={addCategory} 
                                />
                            ))}
                            {edit && !addCategory && <ButtonInTabs
                                onClick={handleAddCategory}
                            >
                                <AddCircleIcon sx={{ my: 1,  color: theme.palette.success.main}} />
                            </ButtonInTabs>}
                        </Tabs>
                        {!addCategory && relevantCategories?.map((category, index) => (
                            <TabPanel key={index} value={(edit ? value > activeForm?.allowedCategories.length - 1 : value > product?.allowedCategories.length - 1) ? 0 : value || 0} index={index} >
                                <Typography sx={{ fontWeight: 'bold'}}>Allowed Items</Typography>
                                <List sx={{
                                    py: 0,
                                    borderColor: 'divider',
                                }}>
                                    <Divider component="li" sx={{ my: 1}} />
                                    {edit
                                        ?
                                        <>
                                            <FormGroup>
                                                {
                                                    category?.allItems?.map((item, index) => (
                                                        <FormControlLabel control={
                                                            <Checkbox
                                                                checked={handleChecked(category, item)}
                                                                disabled={isSubmitting}
                                                                onChange={(event, checked) => handleCategoryItemSelection(event, checked, category.name, item.id)}
                                                            />
                                                        } label={item.name} key={index} />
                                                    ))
                                                }
                                            </FormGroup>
                                            <Divider component="li" sx={{ mt: 1, mb: 2}} />
                                            <ConfirmationDialog
                                                title="Are you sure?"
                                                description="This will remove the category from the group."
                                                confirmButtonText="Remove"
                                                cancelButtonText="Cancel"
                                                response={() => handleRemoveCategory(category)}
                                            >
                                                {(showDialog) => (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: isXs ? 'center' : 'right'
                                                    }}>
                                                        <Button variant="outlined"
                                                                color="error"
                                                                onClick={showDialog}
                                                                disabled={isSubmitting}
                                                                
                                                        >
                                                            Remove Category
                                                        </Button>
                                                    </Box>
                                                    
                                                )}
                                            </ConfirmationDialog>
                                        </>
                                        :
                                        category?.relevantItems?.map((item, index) => (
                                            <React.Fragment key={index}>
                                                <ButtonBase
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleItemClick(item.id, item.name);
                                                    }}
                                                    component="a"
                                                    href={`/Products/${item.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer" 
                                                    sx={{
                                                        width: '100%',
                                                        textDecoration: 'none', 
                                                        color: 'inherit', 
                                                        '&:hover': {
                                                            backgroundColor: 'grey.200',
                                                        }
                                                    }}
                                                >
                                                    <ListItem>
                                                        <ListItemText primary={item.name} />
                                                        <ArrowForwardIcon color='primary' sx={{ ml: 1.5}} />
                                                    </ListItem>
                                                </ButtonBase>
                                                <Divider component="li" />
                                            </React.Fragment>
                                        ))
                                    }
                                </List>
                                {categoryWithNoItemsError &&
                                    <Box>
                                        <Alert variant="filled" severity="warning">
                                            {categoryWithNoItemsError}
                                        </Alert>
                                    </Box>
                                }
                            </TabPanel>
                        ))}
                       
                        {addCategory && edit &&
                            <TabPanel value={value} index={value}>
                                <UpdateProductGroupNewCategory 
                                    setActiveForm={setActiveForm} 
                                    allCategories={allCategories.filter(category =>
                                    !relevantCategories.some(relevantCategory => relevantCategory.id === category.id))}
                                    setAddCategory={setAddCategory}
                                />
                            </TabPanel>
                        }
                    </Box>
                </Grid>
            </Grid>
            {loading && <LoadingOverlay />}
        </Paper>
    )
}

export default ProductGroupCategoriesView;